import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { solutions } from '@/data/solutions';
import { getIcon } from '@/components/common/iconMap';

const SolutionsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 relative bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-sm font-semibold text-[#398024] tracking-wider uppercase mb-3">Our Solutions</h2>
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            IT, telecom, military, and manpower
          </h3>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Planned and implemented through our own resources and specialised partners.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {solutions.map((solution, index) => {
            const Icon = getIcon(solution.icon);
            return (
              <motion.div
                key={solution.slug}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:border-[#398024] hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#398024] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Icon className="text-white" size={32} />
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">{solution.title}</h4>
                <p className="text-gray-600 leading-relaxed mb-6">{solution.description}</p>
                <ul className="space-y-2 mb-6">
                  {solution.items.slice(0, 3).map((item) => (
                    <li key={item} className="flex items-start space-x-2 text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-[#398024] rounded-full mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/solutions"
                  className="inline-flex items-center space-x-2 text-[#398024] hover:text-[#2d6b1a] transition-colors"
                >
                  <span className="text-sm font-medium">View solutions</span>
                  <ArrowRight size={16} />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;
